import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiHeart, FiUsers, FiAward, FiTrendingUp } = FiIcons;

const About = () => {
  React.useEffect(() => {
    document.title = "About Melissa's Hairdressing Salon - Passion Meets Professionalism";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.content = "Learn about Melissa's Hairdressing Salon, where our talented stylists create beauty tailored to you. Experience our passion for hair today!";
    }
  }, []);

  const values = [
    {
      icon: FiHeart,
      title: "Passionate Care",
      description: "We pour our heart into every style, ensuring each client feels valued and beautiful."
    },
    {
      icon: FiUsers,
      title: "Personal Touch",
      description: "Every service is customized to your unique personality and lifestyle needs."
    },
    {
      icon: FiAward,
      title: "Excellence",
      description: "We maintain the highest standards in both technique and customer service."
    },
    {
      icon: FiTrendingUp,
      title: "Innovation",
      description: "We stay current with the latest trends and techniques in hair styling."
    }
  ];

  return (
    <div className="font-inter">
      {/* Hero Section */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="font-playfair text-4xl md:text-6xl font-bold text-gray-900 mb-6">
                About <span className="text-gradient">Melissa's</span><br />
                Hairdressing Salon
              </h1>
              <h2 className="font-playfair text-2xl md:text-3xl font-semibold text-gray-700 mb-6">
                Where Passion Meets Professionalism
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                At Melissa's Hairdressing Salon, we believe in the power of transformation through creative styling and personal care. Our story began with a simple dream: to provide a sanctuary where everyone feels welcomed, inspired, and celebrated.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1562322140-8baeececf3df?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Melissa's Hair Salon interior"
                className="rounded-2xl shadow-2xl w-full"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Journey */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Melissa, salon founder"
                className="rounded-2xl shadow-xl w-full"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h3 className="font-playfair text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Journey
              </h3>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Melissa, our founder, has spent over a decade perfecting her craft, drawing inspiration from her travels and experiences. Her vision is simple yet profound: each client should feel uniquely beautiful and confident.
              </p>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                We are more than just a salon; we are a family of passionate stylists who love to share our expertise and joy in creating exceptional hair experiences.
              </p>
              <div className="bg-gradient-to-r from-amber-50 to-rose-50 p-6 rounded-xl">
                <h4 className="font-playfair text-xl font-semibold text-gray-900 mb-3">
                  Our Promise
                </h4>
                <p className="text-gray-600 leading-relaxed">
                  With our friendly and helpful approach, we are here to listen, advise, and deliver hairstyles that not only flaunt your personality but also inspire confidence and elegance.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gradient-to-br from-rose-50 via-pink-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What We Stand For
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our values guide everything we do, from the moment you walk through our doors to the final touch of your new style.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-lg card-hover text-center"
              >
                <div className="bg-gradient-to-br from-amber-400 to-rose-400 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <SafeIcon icon={value.icon} className="text-white text-2xl" />
                </div>
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Meet Our Expert Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Our talented stylists bring years of experience and a passion for creating beautiful, personalized looks.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                name: "Melissa Johnson",
                role: "Founder & Master Stylist",
                image: "https://images.unsplash.com/photo-1594824763317-3cebc1a9ac0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
                description: "With over 15 years of experience, Melissa specializes in color transformations and bridal styling."
              },
              {
                name: "Sarah Williams",
                role: "Senior Stylist",
                image: "https://images.unsplash.com/photo-1580618672591-eb180b1a973f?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
                description: "Expert in modern cuts and avant-garde styling, Sarah brings creativity to every appointment."
              },
              {
                name: "Emma Davis",
                role: "Color Specialist",
                image: "https://images.unsplash.com/photo-1559582927-62cddd83c107?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
                description: "Emma's expertise in color theory and balayage techniques creates stunning, natural-looking results."
              }
            ].map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-gradient-to-b from-white to-gray-50 p-8 rounded-2xl shadow-lg card-hover text-center"
              >
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-24 h-24 rounded-full mx-auto mb-6 object-cover"
                />
                <h3 className="font-playfair text-xl font-semibold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-amber-600 font-medium mb-4">{member.role}</p>
                <p className="text-gray-600 leading-relaxed">
                  {member.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-rose-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-6">
              Join us at Melissa's Hairdressing Salon and discover the difference!
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Experience the perfect blend of artistry, care, and professionalism that makes every visit special.
            </p>
            <a 
              href="#contact"
              className="bg-white text-amber-600 px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all inline-block"
            >
              Book Your Visit Today
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;